import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search,
  Add,
  FilterList,
} from '@mui/icons-material';
import { SeriesCard } from '../../components/common/Cards';
import { useGame } from '../../context/GameContext';
import { useAuth } from '../../context/AuthContext';
import { seriesService, gameService } from '../../services/apiServices';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const SeriesList = () => {
  const { series, setSeries, setLoading, loading, error, setError } = useGame();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [filteredSeries, setFilteredSeries] = useState([]);

  useEffect(() => {
    fetchSeries();
  }, []);

  useEffect(() => {
    filterSeries();
  }, [series, searchTerm, statusFilter]);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      const response = await seriesService.getAllSeries();
      
      if (response.data && response.data.isSuccess) {
        const seriesData = Array.isArray(response.data.series) ? response.data.series : [];
        
        // Transform API data to match UI expectations
        const transformedSeries = seriesData.map(series => ({
          _id: series._id || series.documentCode,
          id: series.id || series.seriesId,
          name: series.name || 'Unnamed Series',
          description: `${series.name || 'Series'} - ${series.date || 'Date TBD'}`,
          type: 'Cricket', // Default since not in model
          status: determineSeriesStatus(series),
          startDate: formatSeriesDate(series.date, 'start'),
          endDate: formatSeriesDate(series.date, 'end'),
          teams: series.teams?.length || 0,
          matches: series.matches?.length || 0,
          games: series.games?.length || 0,
          venues: series.venues?.length || 0,
          seriesId: series.seriesId,
          date: series.date,
          documentCode: series.documentCode
        }));
        
        setSeries(transformedSeries);
      } else {
        setSeries([]);
        console.warn('No series found in API response');
      }
    } catch (error) {
      console.error('Error fetching series:', error);
      setError('Failed to fetch series');
      toast.error('Failed to fetch series');
      setSeries([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine series status
  const determineSeriesStatus = (series) => {
    const currentDate = new Date();
    const seriesDate = new Date(series.date);
    
    if (series.games && series.games.length > 0) {
      return 'Active';
    } else if (seriesDate > currentDate) {
      return 'Upcoming';
    } else if (seriesDate < currentDate) {
      return 'Completed';
    }
    return 'Draft';
  };

  // Helper function to format series date
  const formatSeriesDate = (dateString, type) => {
    if (!dateString) return type === 'start' ? 'TBD' : 'TBD';
    
    try {
      const date = new Date(dateString);
      if (type === 'start') {
        // For start date, use the provided date
        return date.toISOString().split('T')[0];
      } else {
        // For end date, estimate by adding 2 months
        const endDate = new Date(date);
        endDate.setMonth(endDate.getMonth() + 2);
        return endDate.toISOString().split('T')[0];
      }
    } catch (error) {
      return 'TBD';
    }
  };

  const filterSeries = () => {
    let filtered = Array.isArray(series) ? series : [];

    if (searchTerm) {
      filtered = filtered.filter(s =>
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.seriesId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    setFilteredSeries(filtered);
  };

  const handleViewSeries = (series) => {
    // Use seriesId if available, otherwise fallback to _id
    const seriesIdentifier = series.seriesId || series._id;
    navigate(`/series/${seriesIdentifier}`);
  };

  const handleCreateGame = async (series) => {
    try {
      // Use seriesId if available, otherwise use _id
      const seriesIdentifier = series.seriesId || series._id;
      const response = await gameService.createGame(seriesIdentifier);
      
      if (response.data && response.data.isSuccess) {
        toast.success('Game created successfully!');
        // Navigate to the created game if we have the game data
        if (response.data.out && response.data.out[0]) {
          navigate(`/games/${response.data.out[0]._id}`);
        } else {
          // Fallback to games list
          navigate('/games/my-games');
        }
      } else {
        toast.error('Failed to create game');
      }
    } catch (error) {
      console.error('Error creating game:', error);
      toast.error('Failed to create game');
    }
  };

  const handleCreateSeries = () => {
    navigate('/series/create');
  };

  if (loading && series.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Cricket Series
        </Typography>
        {isAdmin() && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateSeries}
          >
            Create Series
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Controls */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search series..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                startAdornment={<FilterList sx={{ mr: 1 }} />}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="upcoming">Upcoming</MenuItem>
                <MenuItem value="ongoing">Ongoing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={5}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                }}
              >
                Clear Filters
              </Button>
              <Button
                variant="contained"
                onClick={fetchSeries}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Series Grid */}
      {filteredSeries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No series found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || statusFilter
              ? 'Try adjusting your search criteria'
              : 'No cricket series are available at the moment'}
          </Typography>
          {isAdmin() && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateSeries}
              sx={{ mt: 2 }}
            >
              Create First Series
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredSeries.map((seriesItem) => (
            <Grid item xs={12} sm={6} md={4} key={seriesItem._id}>
              <SeriesCard
                series={seriesItem}
                onView={handleViewSeries}
                onCreateGame={handleCreateGame}
                showActions={true}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Loading overlay for refresh */}
      {loading && series.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Container>
  );
};

export default SeriesList;